// Caches the document's shadow roots so a single interaction doesn't re-walk the entire
// DOM for each query. The walk is expensive on large, deeply nested shadow trees and can
// run many times per click, blocking paint and inflating INP. The cache is dropped at the
// next microtask, so it never outlives the current synchronous run or serves a stale result
// across interactions; it assumes the DOM is not mutated between queries within that run.

let cache: ShadowRoot[] | null = null;

const walk = (root: Document | Element): ShadowRoot[] => {
  const shadowRoots: ShadowRoot[] = [];
  const stack: any[] = [root];

  while (stack.length) {
    const current = stack.pop();
    const shadowRoot = current?.shadowRoot;

    if (shadowRoot) {
      shadowRoots.push(shadowRoot);
      stack.push(shadowRoot);
    }

    [...(current?.children ?? [])].forEach((child) => stack.push(child));
  }

  return shadowRoots;
};

export const resetShadowRootCache = () => {
  cache = null;
};

const scheduleReset = () => {
  if (typeof queueMicrotask === 'function') {
    queueMicrotask(resetShadowRootCache);
  } else {
    Promise.resolve().then(resetShadowRootCache);
  }
};

/**
 * Returns all shadow roots under `root`. The default document scan is cached until the
 * next microtask; other roots are walked on demand.
 */
export const getAllShadowRoots = (root: Document | Element = document): ShadowRoot[] => {
  if (root !== document) {
    return walk(root);
  }

  if (!cache) {
    cache = walk(document);
    scheduleReset();
  }

  return cache;
};
